//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

/**
 * Configuration interface for route-based tracing
 * @interface TracingRouteConfig
 * @property {string} pattern - The route pattern to match against
 * @property {number} samplingRate - Sampling rate (0.0 to 1.0) - percentage of transactions to trace
 * @property {'exact' | 'contains' | 'startsWith' | 'endsWith' | 'regex'} matchType - Matching strategy for the route pattern
 * @property {string} [description] - Optional description for documentation
 */
interface TracingRouteConfig {
  pattern: string;
  samplingRate: number;
  matchType: 'exact' | 'contains' | 'startsWith' | 'endsWith' | 'regex';
  description?: string;
}

/**
 * Route-specific tracing configurations
 * 
 * Routes are evaluated in order, first match wins.
 * Higher sampling rates should be used for:
 * - Critical business flows
 * - Routes with known performance issues
 * - Routes under active development/debugging
 * 
 * Lower sampling rates should be used for:
 * - High-traffic routes
 * - Well-established, stable routes
 * - Routes with less business impact
 */
export const TRACING_ROUTE_CONFIGS: TracingRouteConfig[] = [
  {
    pattern: '/listening',
    samplingRate: 1.0,
    matchType: 'contains',
    description: 'Listening dashboard'
  },
  {
    pattern: '/tiktok/mentions',
    samplingRate: 1.0,
    matchType: 'contains',
    description: 'TikTok mentions'
  },
];

export const DEFAULT_SAMPLING_RATE = 0.1;

/**
 * Determines the appropriate sampling rate for a given pathname
 * @param {string} pathname - The current page pathname
 * @returns {number} Sampling rate between 0.0 and 1.0
 */
export function getTracingSamplingRate(pathname: string): number {
  const matchingConfig = TRACING_ROUTE_CONFIGS.find(config =>
    matchesPattern(pathname, config.pattern, config.matchType)
  );
  return matchingConfig?.samplingRate ?? DEFAULT_SAMPLING_RATE;
}

/**
 * Checks if a pathname matches a pattern based on the specified match type
 * @param {string} pathname - The pathname to test
 * @param {string} pattern - The pattern to match against
 * @param {TracingRouteConfig['matchType']} matchType - The type of matching to perform
 * @returns {boolean} True if the pathname matches the pattern
 */
function matchesPattern(pathname: string, pattern: string, matchType: TracingRouteConfig['matchType']): boolean {
  switch (matchType) {
    case 'exact':
      return pathname === pattern;
    case 'contains':
      return pathname.includes(pattern);
    case 'startsWith':
      return pathname.startsWith(pattern);
    case 'endsWith':
      return pathname.endsWith(pattern);
    case 'regex':
      try {
        const regex = new RegExp(pattern);
        return regex.test(pathname);
      } catch (error) {
        console.warn(`Invalid regex pattern in tracing config: ${pattern}`, error);
        return false;
      }
    default:
      return false;
  }
}
